import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Phone, Mail, Award, ShieldCheck, ThumbsUp, ArrowRight } from 'lucide-react'

export default function About() {
  // SEO Focus
  useEffect(() => {
    document.title = 'Om os | Malerfirma Villy Poulsen ApS | Erfaring & Generationsskifte'
    const metaDescriptionTag = document.querySelector('meta[name="description"]')
    if (metaDescriptionTag) {
      metaDescriptionTag.setAttribute(
        'content',
        'Malerfirma Villy Poulsen ApS har mere end 30 års erfaring i branchen. Læs om vores generationsskifte med Camilla og Andreas Poulsen samt vores fokus på kvalitet.'
      )
    }
  }, [])

  const galleryImages = [
    'https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/f3517ae6-43d8-433f-aa89-d1fc1ddd2762.jpeg',
    'https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/05819e56-de71-4634-83f6-601b6e24fc89.jpeg',
    'https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/a700de9a-376f-40eb-8d9a-1bb4d8a2058f.jpeg'
  ]

  const [activeImage, setActiveImage] = useState(null)

  return (
    <div className="bg-white">
      {/* Hero-sektion */}
      <section className="relative bg-[#20252C] text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/9ffac026-6b24-4cce-9dc6-dce86789ca2e.jpeg"
            alt="Om Malerfirma Villy Poulsen ApS"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950/80 via-gray-900/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#01529E]/90 text-white text-xs font-bold uppercase tracking-widest mb-6">
              Hvem Er Vi
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight">
              Om Malerfirma Villy Poulsen ApS
            </h1>
            <p className="mt-4 text-xl text-[#ED1C26] font-bold">
              Erfaring, kvalitet og godt håndværk i Østjylland
            </p>
            <p className="mt-6 text-lg text-gray-300 leading-relaxed">
              Malerfirma Villy Poulsen ApS er et erfarent og kvalitetsbevidst malerfirma med mere end 30 års erfaring i branchen. Gennem årene har vi opbygget et stærkt ry blandt både private kunder, virksomheder, boligforeninger og offentlige institutioner i Østjylland.
            </p>
            <p className="mt-4 text-lg text-gray-300 leading-relaxed">
              Vi er kendt for vores stabile kvalitet, professionelle tilgang og mange tilfredse kunder, som vender tilbage til os igen og igen. Hos os er godt håndværk, troværdighed og personlig service altid en naturlig del af arbejdet.
            </p>
          </div>
        </div>
      </section>

      {/* Sektion: Et moderne malerfirma med generationsskifte */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3 py-1.5 rounded-full">
                Generationsskifte
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900">
                Traditionelt håndværk kombineret med nye idéer
              </h2>
              <p className="text-lg font-bold text-[#01529E]">
                Et moderne malerfirma med fokus på generationsskifte
              </p>
              <div className="space-y-4 text-gray-600 text-sm sm:text-base leading-relaxed">
                <p>
                  I 2023 blev ejerkredsen udvidet med Camilla Poulsen og Andreas Poulsen som led i et planlagt generationsskifte i virksomheden. Begge er udlærte malere med solid erfaring i faget – Camilla blev udlært i 2015 og Andreas i 2016.
                </p>
                <p>
                  Med den nye generation i virksomheden kombinerer vi mange års erfaring med nye idéer, moderne løsninger og et fortsat stærkt fokus på kvalitet og kundeservice. Det sikrer, at Malerfirma Villy Poulsen ApS fortsætter de stolte håndværkstraditioner mange år frem.
                </p>
              </div>
            </div>

            <div>
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/c3e3e326-3ad4-427c-a49d-2f31a1ec5a2b-768x1024.jpg"
                alt="Malerarbejde udført af Malerfirma Villy Poulsen"
                className="rounded-2xl shadow-xl border border-gray-100 object-cover w-full h-[400px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Malerarbejde inde og ude */}
      <section className="py-20 bg-gray-50 border-t border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/7ad70890-421f-4462-9be6-ff81da2bda37.jpeg"
                alt="Malerarbejde inde og ude"
                className="rounded-2xl shadow-xl border border-gray-100 object-cover w-full h-[400px]"
              />
            </div>

            <div className="order-1 lg:order-2 space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3 py-1.5 rounded-full">
                Vores Arbejde
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900">
                Grundigt malerarbejde til både små og store opgaver
              </h2>
              <div className="space-y-4 text-gray-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Vi udfører alle former for malerarbejde – både indendørs og udendørs. Uanset om det drejer sig om en mindre opgave i hjemmet eller en større erhvervsopgave, går vi altid til arbejdet med stor grundighed og sans for detaljen.
                </p>
                <p>
                  Vi lægger vægt på godt forarbejde, kvalitetsmaterialer og løsninger, der både er flotte og holdbare. Fra den første planlægning til den afsluttende aflevering kan du forvente professionelt håndværk og god dialog hele vejen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Professionel rådgivning */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3 py-1.5 rounded-full">
                Ekspert Rådgivning
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900">
                Rådgivning om farver, materialer og vedligeholdelse
              </h2>
              <div className="space-y-4 text-gray-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Hos Malerfirma Villy Poulsen ApS får du ikke kun udført malerarbejde. Du får også professionel rådgivning omkring farvevalg, materialer og vedligeholdelse, så du opnår det bedste resultat til netop din bolig eller virksomhed.
                </p>
                <p>
                  Vi holder os løbende opdateret på nye teknikker, produkter og løsninger i branchen. Det giver dig moderne og holdbare resultater udført med fokus på kvalitet i alle detaljer.
                </p>
              </div>
            </div>

            <div>
              <img
                src="https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/3073aa37-21fb-409d-8135-709df6259f90.jpeg"
                alt="Professionel rådgivning"
                className="rounded-2xl shadow-xl border border-gray-100 object-cover w-full h-[400px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Mød vores team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3 py-1.5 rounded-full">
              Vores Medarbejdere
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mt-4">
              Mød vores dygtige team
            </h2>
            <p className="text-gray-500 mt-2 text-sm sm:text-base">
              Hos Malerfirma Villy Poulsen ApS møder du altid smilende, professionelle og engagerede malere.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { name: 'Villy Poulsen', role: 'Grundlægger & Malermester', phone: '40 63 76 70', img: 'https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/a8677ee3-9c65-41ad-943b-f339f3966dae.jpeg' },
              { name: 'Camilla Poulsen', role: 'Medejer & Maler', phone: '28 59 40 63', img: 'https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/c1e41c6f-59b3-47c6-8b1a-d2b289b241d9.jpeg' },
              { name: 'Andreas Poulsen', role: 'Medejer & Maler', phone: '28 69 40 63', img: 'https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/c0fcff3d-1a91-47f4-882b-44fe46671757.jpeg' },
              { name: 'Jannie Poulsen', role: 'Kontor', phone: '20 70 76 70', img: 'https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/02a11456-82be-4752-83da-b2f5f4821e07.jpeg' }
            ].map((member, idx) => (
              <div key={idx} className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col">
                <div className="h-72 overflow-hidden bg-gray-100">
                  <img
                    src={member.img}
                    alt={member.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6 text-center flex flex-col flex-grow justify-between">
                  <div>
                    <h3 className="text-lg font-black text-gray-900">{member.name}</h3>
                    <p className="text-[#01529E] text-xs font-bold uppercase tracking-wider mt-1">{member.role}</p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <a
                      href={`tel:+45${member.phone.replace(/\s+/g, '')}`}
                      className="text-sm font-bold text-gray-600 hover:text-[#01529E] transition-colors inline-flex items-center gap-2"
                    >
                      <i className="fa-solid fa-phone text-xs text-gray-400" />
                      {member.phone}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Glimt fra vores arbejde */}
      <section className="py-20 bg-gray-50 border-t border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-black text-gray-900">
              Glimt fra vores arbejde
            </h2>
            <p className="text-gray-500 text-sm sm:text-base mt-2">
              Klik på billederne for at se dem i fuld størrelse. Vi leverer altid et professionelt og holdbart resultat.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {galleryImages.map((img, idx) => (
              <div
                key={idx}
                onClick={() => setActiveImage(img)}
                className="group relative h-72 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 bg-white"
              >
                <img
                  src={img}
                  alt={`Malerarbejde galleri ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                  <span className="text-white font-bold text-sm">Vis billede</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA-sektion */}
      <section className="bg-[#20252C] text-white py-16 relative overflow-hidden">
        <div className="absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 w-96 h-96 bg-[#01529E]/10 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-4">
            Kontakt os for et uforpligtende tilbud
          </h2>
          <p className="text-gray-300 text-sm sm:text-base max-w-2xl mx-auto mb-8 leading-relaxed">
            Ønsker du at høre mere om vores arbejde eller få et uforpligtende tilbud på din maleropgave i Østjylland, er du altid velkommen til at kontakte os. Vi står klar med rådgivning, professionel sparring og en løsning, der passer til dine ønsker og behov.
          </p>
          <div className="mt-8">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#01529E] text-white font-bold hover:bg-[#01529E]/90 transition-all shadow-lg cursor-pointer"
            >
              Kontakt os
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setActiveImage(null)}
        >
          <button
            onClick={() => setActiveImage(null)}
            className="absolute top-6 right-6 text-white hover:text-gray-300 text-3xl font-light cursor-pointer focus:outline-none"
          >
            &times;
          </button>
          <div className="max-w-5xl max-h-[85vh] relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={activeImage}
              alt="Malerarbejde fuld størrelse"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  )
}
